import React, { useState } from "react";
import styled from "styled-components";
import { API_ENDPOINTS } from "../config/api";

import Button from "../components/Button";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/Input";
import { Card, CardTitle } from "../components/Card";
import PageContent from "../components/PageContent";
import { Form, FormGroup, Label, FieldDescription } from "../components/Form";
import { ErrorState, SuccessState } from "../components/CommonStates";

const PasswordChangeBanner = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: #856404;

  h4 {
    margin: 0 0 0.5rem 0;
    color: #856404;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
`;

function Settings({ user, onBack, onLogout }) {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [emailFormData, setEmailFormData] = useState({
    newEmail: "",
    currentPasswordForEmail: ""
  });
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [currentUser, setCurrentUser] = useState(user);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEmailChange = (e) => {
    setEmailFormData({
      ...emailFormData,
      [e.target.name]: e.target.value
    });
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    // Check minimum length
    if (formData.newPassword.length < 8) {
      setError("New password must be at least 8 characters long");
      return false;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords do not match");
      return false;
    }

    return true;
  };

  const validateEmailForm = () => {
    if (!validateEmail(emailFormData.newEmail)) {
      setEmailError("Please enter a valid email address");
      return false;
    }

    if (emailFormData.newEmail === currentUser.username) {
      setEmailError("New email must be different from current email");
      return false;
    }

    if (!emailFormData.currentPasswordForEmail) {
      setEmailError("Current password is required to change email");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.updateProfile, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessage("Password updated successfully!");
        // Update the current user to clear the requirePasswordChange flag
        if (data.user) {
          setCurrentUser(data.user);
        }
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        });
      } else {
        setError(data.error || "Update failed");
      }
    } catch (error) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");
    setEmailMessage("");

    if (!validateEmailForm()) {
      return;
    }

    setEmailLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.updateProfile, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: emailFormData.currentPasswordForEmail,
          newUsername: emailFormData.newEmail
        })
      });

      const data = await response.json();

      if (response.ok) {
        setEmailMessage("Email updated successfully!");
        setCurrentUser({ ...currentUser, username: emailFormData.newEmail });
        setEmailFormData({
          newEmail: "",
          currentPasswordForEmail: ""
        });
      } else {
        setEmailError(data.error || "Email update failed");
      }
    } catch (error) {
      setEmailError("Network error. Please try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <>
      <PageHeader title="Settings" showBackButton={true} onBack={onBack} />

      <PageContent>
        {currentUser.requirePasswordChange && (
          <PasswordChangeBanner>
            <h4>⚠️ Password Reset Required</h4>
            <p>
              Your password has been reset by an administrator. Please change
              your password below to continue using your account.
            </p>
          </PasswordChangeBanner>
        )}

        <Card>
          <CardTitle>
            {currentUser.requirePasswordChange
              ? "⚠️ Change Password (Required)"
              : "Change password"}
          </CardTitle>
          {error && <ErrorState>{error}</ErrorState>}
          {message && <SuccessState>{message}</SuccessState>}

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="currentPassword">Current password</Label>
              <Input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="newPassword">New password</Label>
              <Input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
                disabled={loading}
                minLength="8"
              />
              <FieldDescription>
                Password must be at least 8 characters long
              </FieldDescription>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm new password</Label>
              <Input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading}
                minLength="8"
              />
            </FormGroup>

            <Button type="submit" disabled={loading} fullWidth>
              {loading ? "Updating..." : "Update"}
            </Button>
          </Form>
        </Card>

        <Card>
          <CardTitle>Change email</CardTitle>
          {emailError && <ErrorState>{emailError}</ErrorState>}
          {emailMessage && <SuccessState>{emailMessage}</SuccessState>}

          <Form onSubmit={handleEmailSubmit}>
            <FormGroup>
              <Label htmlFor="newEmail">New email</Label>
              <Input
                type="email"
                id="newEmail"
                name="newEmail"
                value={emailFormData.newEmail}
                onChange={handleEmailChange}
                required
                disabled={emailLoading}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="currentPasswordForEmail">Current password</Label>
              <Input
                type="password"
                id="currentPasswordForEmail"
                name="currentPasswordForEmail"
                value={emailFormData.currentPasswordForEmail}
                onChange={handleEmailChange}
                required
                disabled={emailLoading}
              />
            </FormGroup>

            <Button type="submit" disabled={emailLoading} fullWidth>
              {emailLoading ? "Updating..." : "Update"}
            </Button>
          </Form>
        </Card>

      </PageContent>
    </>
  );
}

export default Settings;
